import { useState } from 'react';
import { isAxiosError } from 'axios';
import {
  CONNECTOR_CATALOGUE_SOURCES,
  CONNECTOR_EXPORT_OWNERS,
  CONNECTOR_LOYALTY_SOURCES,
  STORE_PASS_LIFETIME_MAX_MINUTES,
  STORE_PASS_LIFETIME_MIN_MINUTES,
  type StoreLinkSettings,
} from '@reform-lite/contracts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { useT } from '@/i18n/use-t';
import {
  useConnectorLocationSettings,
  useUpdateConnectorLocationSettings,
  type ConnectorLocationSettings,
} from '@/hooks/use-connector-location-settings';

/**
 * Per-store connector ownership (RL-452): where a store's tills get the
 * connector catalogue, who writes its documents into the store's system, and
 * (RL-570) where they look loyalty customers up — Back Office, or the store's
 * Link through its CRM loyalty source (integration profile 3). The server
 * refuses `link` without an active Link and an enabled profile 3 source, and
 * that refusal is shown on the row.
 *
 * One row per store and one save per row, because an owner switch is refused
 * by the server until the current owner's queue is empty — the queue is shown
 * next to the choice so the operator can see why before pressing save, and
 * the server's refusal is shown on the row it belongs to.
 *
 * SSC-264: the SecureScale source is editable only for a tenant licensed for
 * SecureScale; the server refuses `link` otherwise and never delivers it. The
 * stored value is still shown.
 */
export function ConnectorLocationSettingsPanel({
  canEdit,
  secureScaleLicensed,
}: {
  canEdit: boolean;
  secureScaleLicensed: boolean;
}): JSX.Element {
  const t = useT('admin_ui');
  const query = useConnectorLocationSettings(true);

  return (
    <div className="space-y-3 border-t border-border pt-5">
      <div>
        <h3 className="text-sm font-semibold">{t('settings.integrations.ownership.title')}</h3>
        <p className="max-w-3xl text-xs text-muted-foreground">
          {t('settings.integrations.ownership.description')}
        </p>
      </div>
      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">
          {t('settings.integrations.ownership.loading')}
        </p>
      ) : query.isError ? (
        <p className="text-sm text-destructive">{t('settings.integrations.ownership.loadError')}</p>
      ) : (query.data ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('settings.integrations.ownership.empty')}
        </p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>{t('settings.integrations.ownership.store')}</TH>
              <TH>{t('settings.integrations.ownership.catalogueSource')}</TH>
              <TH>{t('settings.integrations.ownership.secureScaleSource')}</TH>
              <TH>{t('settings.integrations.ownership.exportOwner')}</TH>
              <TH>{t('settings.integrations.ownership.loyaltySource')}</TH>
              <TH>{t('settings.integrations.ownership.lifetime')}</TH>
              <TH className="text-right">{t('settings.integrations.actions')}</TH>
            </TR>
          </THead>
          <TBody>
            {(query.data ?? []).map((row) => (
              <OwnershipRow
                key={`${row.locationId}:${row.updatedAt ?? ''}`}
                row={row}
                canEdit={canEdit}
                secureScaleLicensed={secureScaleLicensed}
              />
            ))}
          </TBody>
        </Table>
      )}
    </div>
  );
}

function OwnershipRow({
  row,
  canEdit,
  secureScaleLicensed,
}: {
  row: ConnectorLocationSettings;
  canEdit: boolean;
  secureScaleLicensed: boolean;
}): JSX.Element {
  const t = useT('admin_ui');
  const update = useUpdateConnectorLocationSettings();
  const [catalogueSource, setCatalogueSource] = useState(row.catalogueSource);
  const [secureScaleSource, setSecureScaleSource] = useState(row.secureScaleSource ?? 'backoffice');
  const [exportOwner, setExportOwner] = useState(row.exportOwner);
  // RL-570: absent on the row reads as Back Office (an older server).
  const storedLoyalty = row.loyaltySource ?? 'backoffice';
  const [loyaltySource, setLoyaltySource] = useState(storedLoyalty);
  const [lifetime, setLifetime] = useState(String(row.storePassLifetimeMinutes));
  const [error, setError] = useState<string | null>(null);

  const minutes = Number(lifetime);
  const lifetimeValid =
    /^\d+$/.test(lifetime.trim()) &&
    minutes >= STORE_PASS_LIFETIME_MIN_MINUTES &&
    minutes <= STORE_PASS_LIFETIME_MAX_MINUTES;
  const changed =
    catalogueSource !== row.catalogueSource ||
    secureScaleSource !== (row.secureScaleSource ?? 'backoffice') ||
    exportOwner !== row.exportOwner ||
    loyaltySource !== storedLoyalty ||
    minutes !== row.storePassLifetimeMinutes;
  const id = (field: string) => `ownership-${row.locationId}-${field}`;

  async function save(): Promise<void> {
    setError(null);
    const settings: StoreLinkSettings = {
      catalogueSource,
      ...(secureScaleSource !== (row.secureScaleSource ?? 'backoffice')
        ? { secureScaleSource }
        : {}),
      exportOwner,
      storePassLifetimeMinutes: minutes,
      // RL-570: sent only when the operator changed it. Absent keeps the
      // stored value, so a save of the other settings from a screen loaded
      // before somebody else moved the store's loyalty cannot move it back.
      ...(loyaltySource !== storedLoyalty ? { loyaltySource } : {}),
    };
    try {
      await update.mutateAsync({ locationId: row.locationId, settings });
    } catch (saveError) {
      const body = isAxiosError(saveError)
        ? (saveError.response?.data as { message?: string } | undefined)
        : undefined;
      setError(body?.message ?? t('settings.integrations.ownership.saveError'));
    }
  }

  return (
    <TR>
      <TD>
        <div className="font-medium">{row.location.name}</div>
        <div className="text-xs text-muted-foreground">{row.location.code}</div>
      </TD>
      <TD>
        <Select
          id={id('catalogue')}
          aria-label={t('settings.integrations.ownership.catalogueSource')}
          value={catalogueSource}
          disabled={!canEdit}
          onChange={(event) => setCatalogueSource(event.target.value as typeof catalogueSource)}
        >
          {CONNECTOR_CATALOGUE_SOURCES.map((value) => (
            <option key={value} value={value}>
              {t(`settings.integrations.ownership.owner.${value}`)}
            </option>
          ))}
        </Select>
      </TD>
      <TD>
        <Select
          id={id('securescale')}
          aria-label={t('settings.integrations.ownership.secureScaleSource')}
          value={secureScaleSource}
          disabled={!canEdit || !secureScaleLicensed}
          onChange={(event) => setSecureScaleSource(event.target.value as typeof secureScaleSource)}
        >
          {CONNECTOR_CATALOGUE_SOURCES.map((value) => (
            <option key={value} value={value}>
              {t(`settings.integrations.ownership.owner.${value}`)}
            </option>
          ))}
        </Select>
      </TD>
      <TD>
        <Select
          id={id('export')}
          aria-label={t('settings.integrations.ownership.exportOwner')}
          value={exportOwner}
          disabled={!canEdit}
          onChange={(event) => setExportOwner(event.target.value as typeof exportOwner)}
        >
          {CONNECTOR_EXPORT_OWNERS.map((value) => (
            <option key={value} value={value}>
              {t(`settings.integrations.ownership.owner.${value}`)}
            </option>
          ))}
        </Select>
        <QueueLine row={row} />
      </TD>
      <TD>
        <Select
          id={id('loyalty')}
          aria-label={t('settings.integrations.ownership.loyaltySource')}
          value={loyaltySource}
          disabled={!canEdit}
          onChange={(event) => setLoyaltySource(event.target.value as typeof loyaltySource)}
        >
          {CONNECTOR_LOYALTY_SOURCES.map((value) => (
            <option key={value} value={value}>
              {t(`settings.integrations.ownership.owner.${value}`)}
            </option>
          ))}
        </Select>
        {loyaltySource === 'link' && (
          <p className="mt-1 max-w-xs text-xs text-muted-foreground">
            {t('settings.integrations.ownership.loyaltyLinkHint')}
          </p>
        )}
      </TD>
      <TD>
        <Input
          id={id('lifetime')}
          aria-label={t('settings.integrations.ownership.lifetime')}
          inputMode="numeric"
          value={lifetime}
          disabled={!canEdit}
          onChange={(event) => setLifetime(event.target.value)}
          className="w-28 font-mono"
        />
        <p
          className={`mt-1 text-xs ${lifetimeValid ? 'text-muted-foreground' : 'text-destructive'}`}
        >
          {t('settings.integrations.ownership.lifetimeHint')}
        </p>
      </TD>
      <TD className="text-right">
        {canEdit && (
          <Button
            type="button"
            variant="outline"
            disabled={!changed || !lifetimeValid || update.isPending}
            onClick={() => void save()}
          >
            {t('settings.integrations.ownership.save')}
          </Button>
        )}
        {error !== null && (
          <p role="alert" className="mt-2 max-w-xs text-left text-xs text-destructive">
            {error}
          </p>
        )}
      </TD>
    </TR>
  );
}

/** The current export owner's queue — what a switch away from it waits on. */
function QueueLine({ row }: { row: ConnectorLocationSettings }): JSX.Element | null {
  const t = useT('admin_ui');
  if (row.exportOwner === 'off') return null;
  const queue = row.exportOwner === 'backoffice' ? row.backofficeQueue : row.linkQueue;
  if (row.exportOwner === 'link' && queue.reportedAt === null) {
    return (
      <p className="mt-1 text-xs text-muted-foreground">
        {t('settings.integrations.ownership.queueUnknown')}
      </p>
    );
  }
  const waiting = queue.pending + queue.failed + queue.undiscovered;
  return (
    <p className="mt-1 text-xs text-muted-foreground">
      {waiting === 0
        ? t('settings.integrations.ownership.queueEmpty')
        : `${t('settings.integrations.ownership.queueWaiting')}: ${waiting}`}
    </p>
  );
}
